import { lessonSelectors, lessonTaskSelectors } from 'common/modules/ReactShared';
import { AnimationControls, motion as m } from 'framer-motion';
import { Translate } from 'i18n';
import React, { SyntheticEvent, useCallback, useImperativeHandle, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useTypedSelector } from 'students/stores/_utils';
import rightArrowIcon from 'students/views/shared/assets/icons/arrow_right_icon.svg';
import ScrollToBottomButton, {
  tScrollToBottomButtonRef
} from 'students/views/shared/components/ScrollToBottomButton';
import Spinner from 'students/views/shared/components/Spinner/Spinner';
import UrlIcon from 'students/views/shared/components/UrlIcon';
import { getBackDestinationUrl } from 'students/views/shared/helpers';

export type tLearnTaskControlsRef = {
  update: () => void;
};

interface IProps {
  onFinish: () => void;
  isCompleting: boolean;
  previousTaskId?: number | null;
  animate?: AnimationControls;
  showScrollButton?: boolean;
}

const LearnTaskControls = React.forwardRef<tLearnTaskControlsRef, IProps>(
  (
    { onFinish, isCompleting, previousTaskId, animate, showScrollButton },
    forwardedRef
  ) => {
    const history = useHistory();
    const taskSession = useTypedSelector(lessonTaskSelectors.selectLessonTaskSession);
    const lessonSession = useTypedSelector(lessonSelectors.selectLessonSession);
    const finishTaskButtonRef = useRef<HTMLButtonElement>(null);
    const scrollButtonRef = useRef<tScrollToBottomButtonRef>(null);
    const initialAnimationState = animate ? { opacity: 0 } : { opacity: 1 };

    useImperativeHandle(forwardedRef, () => ({
      update() {
        scrollButtonRef.current?.update();
      }
    }));

    const handleFinish = useCallback(
      (event: SyntheticEvent) => {
        event.preventDefault();
        onFinish();
      },
      [onFinish]
    );

    const handlePreviousTaskClick = useCallback(
      (event: SyntheticEvent) => {
        event.preventDefault();
        const destination = getBackDestinationUrl(
          taskSession,
          lessonSession,
          window.location.pathname
        );
        if (destination) history.push(destination);
      },
      [history, taskSession, lessonSession]
    );

    return (
      <>
        <m.div
          className="ln-flex ln-items-center ln-gap-5 ln-m-0 ln-py-8 ln-px-[5%] ln-w-full tablet:ln-justify-center tablet:ln-py-10 tablet:ln-px-0 tablet:ln-bg-transparent"
          initial={initialAnimationState}
          animate={animate}
        >
          {previousTaskId ? (
            <button
              type="button"
              onClick={handlePreviousTaskClick}
              className="ln-inline-flex ln-items-center ln-justify-center ln-select-none ln-cursor-pointer ln-p-0 ln-m-0 ln-rounded-full ln-text-white ln-font-medium ln-text-lg ln-leading-9 ln-h-14 ln-bg-black/40 hover:ln-bg-black/60 ln-transition-colors ln-duration-300 ln-w-[104px] ln-min-w-[104px] tablet:ln-w-[252px]"
            >
              <UrlIcon
                url={rightArrowIcon}
                color="#FFFFFF"
                width="1em"
                height="1em"
                className="ln-rotate-180"
              />
              <span className="ln-ms-2.5 ln-hidden tablet:ln-inline">
                <Translate str="frontend.lesson_task.check.navigation_previous" />
              </span>
            </button>
          ) : null}

          <button
            data-testid="students/components/TaskSubjects/Learn/FinishButton"
            type="button"
            onClick={handleFinish}
            disabled={isCompleting}
            ref={finishTaskButtonRef}
            className="ln-inline-flex ln-items-center ln-justify-center ln-select-none ln-cursor-pointer ln-p-0 ln-m-0 ln-border ln-border-button-yellow-bg ln-rounded-[62px] ln-shadow-[0_4px_10px_rgba(13,97,120,0.2)] ln-bg-button-yellow-bg ln-text-[#00466F] ln-font-semibold ln-text-xl ln-leading-9 ln-uppercase ln-h-14 ln-w-full hover:ln-bg-button-yellow-bg-hover hover:ln-border-button-yellow-bg-hover active:ln-bg-[var(--lingu-yellow-btn-bg-pressed)] focus:ln-outline-none focus:ln-ring-[3px] focus:ln-ring-blue-800 focus:ln-ring-offset-[3px] disabled:ln-opacity-20 disabled:ln-border-0 disabled:ln-pointer-events-none tablet:ln-w-[252px]"
          >
            {isCompleting ? (
              <Spinner variant="darkBlue" />
            ) : (
              <>
                {/* span wrapping is needed here for solving node unmount removing error */}
                <span className="ln-me-2.5">
                  <Translate str="frontend.lesson_task.next_button" />
                </span>
                <UrlIcon
                  className="dir-auto"
                  url={rightArrowIcon}
                  color="#00466F"
                  width="1em"
                  height="1em"
                />
              </>
            )}
          </button>
        </m.div>

        {showScrollButton && (
          <ScrollToBottomButton ref={scrollButtonRef} targetRef={finishTaskButtonRef} />
        )}
      </>
    );
  }
);

export default LearnTaskControls;
